import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";

const memberSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string().nullable(),
  role: z.string().nullable(),
  isTeamLead: z.boolean(),
});
const teamSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  memberCount: z.number(),
  members: z.array(memberSchema),
});
const userSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string().nullable(),
  role: z.string().nullable(),
  status: z.string().nullable(),
  teams: z.array(z.string()),
  lastLogin: z.string().nullable(),
});
const responseSchema = z.object({
  users: z.array(userSchema),
  teams: z.array(teamSchema),
  canManage: z.boolean(),
  currentUserId: z.number().nullable(),
});
export type TeamsUsersResponse = z.infer<typeof responseSchema>;

export function useTeamsUsersQuery() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  return useQuery({
    queryKey: ["teams-users", selectedCustomer?.id, selectedFacility?.id],
    enabled: Boolean(selectedCustomer && selectedFacility),
    queryFn: async () => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ]["teams-users"].$get({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
        },
      });
      if (!response.ok) {
        const detail = await response.text();
        throw new Error(
          `Failed to load Teams and Users (${response.status}): ${detail || "The Work API returned an empty error."}`,
        );
      }
      return responseSchema.parse(await response.json());
    },
    meta: { errorMessage: "Failed to load Teams and Users." },
  });
}
